import { Unauthorized } from "@app/shared/ErrorPages";
import { FC, useContext, useEffect, useState } from "react";
import { FlyoutLink } from "@app/shared/Flyouts";
import { Link } from "react-router-dom";
import { EcdReportGenerationRouteState } from "./EcdReportGenerationRouteState";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import { BreadcrumbTemplateEnum } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageHeaderRight from "@app/shared/PageHeaderRight";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import { UserContext } from "@app/user/UserContext";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import Roles from "@app/user/roles";
import EnterpriseComplianceDashboardContextProvider, { EnterpriseComplianceDashboardContext } from "@app/overviews/enterprise-compliance-dashboard/contexts/EcdContext";
import EnterpriseComplianceFiltersContextProvider, { EnterpriseComplianceFiltersContext } from "@app/overviews/enterprise-compliance-dashboard/contexts/EcdFiltersContext";
import EcdColorsContextProvider, { EcdColorsContext } from "@app/overviews/enterprise-compliance-dashboard/contexts/EcdColorsContext";
import EnterpriseComplianceDashboardFilters from "@app/overviews/enterprise-compliance-dashboard/EcdFilters";
import { EcdCustomizeColorsFlyout } from "@app/overviews/enterprise-compliance-dashboard/flyouts/EcdCustomizeColorsFlyout";
import AdequacyBreakdown from "@app/overviews/enterprise-compliance-dashboard/tiles/AdequacyBreakdown";
import CompositeFillableGaps from "@app/overviews/enterprise-compliance-dashboard/tiles/CompositeFillableGaps";
import CountyStatusOverview from "@app/overviews/enterprise-compliance-dashboard/tiles/CountyStatusOverview";
import ExceptionsTile from "@app/overviews/enterprise-compliance-dashboard/tiles/ExceptionsTile";
import GapOverview from "@app/overviews/enterprise-compliance-dashboard/tiles/GapOverview";
import GapsByAge from "@app/overviews/enterprise-compliance-dashboard/tiles/GapsByAge";
import LineOfBusinessOverview from "@app/overviews/enterprise-compliance-dashboard/tiles/LineOfBusinessOverview";
import TopCountiesByGap from "@app/overviews/enterprise-compliance-dashboard/tiles/TopCountiesByGap";
import TopSpecialtiesByGap from "@app/overviews/enterprise-compliance-dashboard/tiles/TopSpecialtiesByGap";

const WrappedEnterpriseComplianceDashboard: FC = () => {
    const user = useContext(UserContext);
    const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;
    const dashboardContext = useContext(EnterpriseComplianceDashboardContext);
    const filtersContext = useContext(EnterpriseComplianceFiltersContext);
    const colorsContext = useContext(EcdColorsContext);
    const [ecdColorsFlyoutHasChanged, setEcdColorsFlyoutHasChanged] = useState(false);

    if (!user.roles.includes(Roles.ENTERPRISE_COMPLIANCE_DASHBOARD_USER)) {
        return <Unauthorized />;
    }
    const [isDesktopSized, setIsDesktopSized] = useState(window.matchMedia("(min-width: 1200px)").matches);

    useEffect(() => {
        const mql = window.matchMedia("(min-width: 1200px)");
        const resizeListener = (e: MediaQueryListEvent) => {
            setIsDesktopSized(e.matches);
        }
        mql.addEventListener("change", resizeListener);
    }, []);

    return (
        <Page documentTitle="Enterprise Compliance Dashboard">
            <Breadcrumbs template={BreadcrumbTemplateEnum.Overviews} items={["Enterprise Compliance Dashboard"]} />
            <PageInner>
                <PageHeader title="Enterprise Compliance Dashboard" disableHR={true}>
                    <PageHeaderRight>
                        {featureFlags.has(Features.ECD_COLOR_CUSTOMIZATION) && (
                          <FlyoutLink
                            content={{
                              body: <EcdCustomizeColorsFlyout onChange={(change: boolean) => setEcdColorsFlyoutHasChanged(change)} />,
                              title: "Customize Colors",
                            }}
                            classNames="qes-button-secondary"
                            testId="enterprise-compliance-dashboard__customize-colors"
                            warnOnClose={ecdColorsFlyoutHasChanged}
                            modalContent={{
                              body: "You will lose unsaved changes. Continue with closing the sidebar?",
                              cancelButtonText: "Cancel",
                              confirmButtonText: "Continue",
                              title: "Warning",
                            }}
                          >
                            Customize Colors
                          </FlyoutLink>
                         )}
                        {dashboardContext.showExportButton && (
                          <Link<EcdReportGenerationRouteState> to={{
                            pathname: "/overviews/enterprise-compliance-dashboard/generate-report",
                            state: {
                              adequacyBreakdownData: dashboardContext.adequacyBreakdownData,
                              apiReadyFilters: filtersContext.apiReadyFilters,
                              arrowNegative: colorsContext.arrowNegative,
                              arrowPositive: colorsContext.arrowPositive,
                              compositeFillableGapsData: dashboardContext.compositeFillableGapsData,
                              countyStatusData: dashboardContext.countyStatusData,
                              defaultColors: colorsContext.defaultColors,
                              displayFilters: filtersContext.displayFilters,
                              exceptionsArray: colorsContext.exceptionsArray,
                              exceptionsData: dashboardContext.exceptionsData,
                              exceptionsPrimary: colorsContext.exceptionsPrimary,
                              exceptionsTimeFrame: filtersContext.exceptionsTimeFrame,
                              fillableGapsPrimary: colorsContext.fillableGapsPrimary,
                              fillableGapsSecondary: colorsContext.fillableGapsSecondary,
                              gapOverviewData: dashboardContext.gapOverviewData,
                              gapOverviewTimeFrame: filtersContext.gapOverviewTimeFrame,
                              gapsArray: colorsContext.gapsArray,
                              gapsByAgeData: dashboardContext.gapsByAgeData,
                              gapsByAgeTimeFrame: filtersContext.gapsByAgeTimeFrame,
                              gapsBySpecialtyData: dashboardContext.gapsBySpecialtyData,
                              gapsPrimary: colorsContext.gapsPrimary,
                              lineOfBusinessData: dashboardContext.lineOfBusinessData,
                              topCountiesByGapData: dashboardContext.topCountiesByGapData,
                              unfillableGaps: colorsContext.unfillableGaps
                            },
                          }}
                            className="qes-button qes-button-primary mt-1 mr5" type="button" data-testid="enterprise-compliance-dashboard__export">Export</Link>
                        )}
                    </PageHeaderRight>
                </PageHeader>
                <PageSection>
                    <EnterpriseComplianceDashboardFilters isDesktopSized={isDesktopSized} />
                    {dashboardContext.showNoResultsAlert && (
                        <QESAlert type="info" persist={true} classNames="mb-3">
                            There is no data for the current combination of applied filters.
                        </QESAlert>
                    )}
                    <div className="enterprise-compliance-dashboard__content row">
                        {/* Row 1 */}
                        <AdequacyBreakdown />
                        {isDesktopSized ? (
                            <>
                                <GapOverview />
                                <CompositeFillableGaps />
                            </>
                        ) : (
                            <>
                                <CompositeFillableGaps />
                                <GapOverview />
                            </>
                        )}
                        {/* Row 2 */}
                        <GapsByAge />
                        <ExceptionsTile />
                        {/* Row 3 */}
                        <TopSpecialtiesByGap />
                        <TopCountiesByGap />
                        {/* Row 4 */}
                        <LineOfBusinessOverview />
                        <CountyStatusOverview />
                    </div>
                </PageSection>
            </PageInner>
        </Page>
    );
};

const EnterpriseComplianceDashboard: FC = () => (
    <EnterpriseComplianceFiltersContextProvider>
        <EnterpriseComplianceDashboardContextProvider>
            <EcdColorsContextProvider>
                <WrappedEnterpriseComplianceDashboard />
            </EcdColorsContextProvider>
        </EnterpriseComplianceDashboardContextProvider>
    </EnterpriseComplianceFiltersContextProvider>
);

export default EnterpriseComplianceDashboard;
